import Link from 'components/common/link';
import { routePaths } from 'appConfig';
import { aboutUsItems, guidesItems, howItWorksItems } from './utils';
import { useYBPerksContext } from 'lib/yb-perks/YBPerksContextProvider';
import { motion, useAnimation } from 'framer-motion';
import { useEffect, useState } from 'react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import {
  isClickAndCollectEnabled,
  isResumedClickAndCollectNoticeEnabled,
} from 'lib/click-and-collect';
import { Badge } from 'react-bootstrap';

export default function MainMenu({
  showPerks = true,
  cateringEnabled,
  ybExpressEnabled,
}: {
  showPerks?: boolean;
  cateringEnabled?: boolean;
  ybExpressEnabled?: boolean;
}) {
  const { totalUnlockablePerks } = useYBPerksContext();
  const [isPerkViewed, setIsPerkViewed] = useState(false);

  const controls = useAnimation();
  const { pathname } = useRouter();

  useEffect(() => {
    let cycleCount = 0;
    let interval: NodeJS.Timeout;

    const animateCycle = async () => {
      await controls.start({
        rotate: [0, -5, 5, -5, 5, -2, 2, 0],
        transition: {
          duration: 0.4,
          ease: 'easeInOut',
        },
      });

      if (!isPerkViewed) {
        cycleCount++;
        if (cycleCount >= 3) {
          cycleCount = 0;

          interval = setTimeout(animateCycle, 600);
        } else {
          animateCycle();
        }
      } else {
        controls.stop();
        controls.set({ rotate: 0 });
      }
    };

    animateCycle();

    return () => clearTimeout(interval);
  }, [controls, isPerkViewed]);

  return (
    <nav className="main-nav">
      <ul className="menu sf-arrows">
        <li>
          <a
            style={{ cursor: 'pointer' }}
            className={cn(
              'sf-with-ul',
              aboutUsItems.some((item) => item.to === pathname)
                ? 'tw-text-primary'
                : '',
            )}
          >
            About Us
          </a>

          <ul>
            {aboutUsItems
              .filter((item) => {
                if (
                  !cateringEnabled &&
                  item.to === routePaths.CATERING
                )
                  return false;
                if (
                  !ybExpressEnabled &&
                  item.to === routePaths.YB_EXPRESS
                )
                  return false;
                return true;
              })
              .map((item) => (
                <li key={item.to}>
                  <Link to={item.to} activeClassName="active">
                    <>{item.label}</>
                  </Link>
                </li>
              ))}
          </ul>
        </li>

        <li>
          <a
            className={cn(
              'sf-with-ul',
              howItWorksItems.some((item) => item.to === pathname)
                ? 'tw-text-primary'
                : '',
            )}
            style={{ cursor: 'pointer' }}
          >
            How It Works
          </a>

          <ul>
            {howItWorksItems.map((item) => (
              <li key={item.to}>
                <Link to={item.to} activeClassName="active">
                  <div className="tw-flex tw-items-center tw-justify-between">
                    {item.label}
                    {item.to === routePaths.CLICK_AND_COLLECT && (
                      <>
                        {!isClickAndCollectEnabled() ? (
                          <Badge className="tw-uppercase tw-bg-primary tw-text-white tw-tracking-widest tw-px-3 tw-m-0 tw-leading-none">
                            Paused
                          </Badge>
                        ) : (
                          isResumedClickAndCollectNoticeEnabled() && (
                            <Badge className="tw-uppercase tw-bg-primary tw-text-white tw-tracking-widest tw-px-2 tw-m-0 tw-leading-none">
                              Resumed
                            </Badge>
                          )
                        )}
                      </>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </li>

        <li>
          <a
            className={cn(
              'sf-with-ul',
              guidesItems.some((item) => item.to === pathname)
                ? 'tw-text-primary'
                : '',
            )}
            style={{ cursor: 'pointer' }}
          >
            Guides & FAQs
          </a>

          <ul>
            {guidesItems.map((item) => (
              <li key={item.to}>
                <Link to={item.to} activeClassName="active">
                  <>{item.label}</>
                </Link>
              </li>
            ))}
          </ul>
        </li>

        <li>
          <Link to={routePaths.CATEGORIES} activeClassName="active">
            <>Order Now</>
          </Link>
        </li>

        {showPerks && (
          <li>
            <motion.a
              className="sf-with-ul after:tw-content-none"
              style={{
                cursor: 'pointer',
                rotate: isPerkViewed ? 0 : undefined,
              }}
              animate={
                isPerkViewed
                  ? { rotate: 0, transition: { duration: 0 } }
                  : controls
              }
              transition={{
                duration: 0.5,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              onMouseEnter={() => setIsPerkViewed(true)}
            >
              <div className="tw-relative">
                <i className="la la-gift tw-text-[3rem]" />
                <span className="tw-min-w-[1.7rem] tw-h-[1.7rem] tw-bg-[#222] tw-rounded-[50%] tw-text-white tw-absolute tw-flex tw-items-center tw-justify-center tw-leading-none tw-text-base tw-top-0 -tw-right-2">
                  {totalUnlockablePerks}
                </span>
              </div>
            </motion.a>
            <ul className="tw-p-8 tw-left-[unset] tw-right-0">
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                <span className="tw-font-semibold">YB Perks</span>
              </div>
              <div className="tw-text-xl tw-text-[#666] tw-mb-8">
                Unlock exclusive rewards and benefits.
              </div>

              <Link
                to={routePaths.YB_PERKS}
                className="tw-bg-primary tw-text-white tw-px-4 tw-py-2 tw-transition-all hover:tw-bg-primary-600 tw-rounded-md"
              >
                <>Explore YB Perks →</>
              </Link>
            </ul>
          </li>
        )}
      </ul>
    </nav>
  );
}
